import React from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import { formatMetric } from "../visuals/formatMetric";

/**
 * Ordered stages with declining volume.
 *
 * The block exists to answer "which step is losing people", so step-to-step
 * conversion is the primary number in the larger type. Cumulative-from-original
 * is secondary and smaller: cohort percentages decline monotonically and hide
 * WHERE the loss happens — every late stage looks equally bad.
 *
 * Bar width encodes the cohort share (so the funnel tapers truthfully), while
 * the highlighted stage is the worst step-to-step drop, not the smallest bar.
 */
const FunnelBlock = observer(({ block }) => {
  const stages = (block.stages || []).filter(
    stage => stage && stage.value != null && Number.isFinite(Number(stage.value))
  );
  if (stages.length < 2) return null;

  const original = Number(stages[0].value);
  if (!original) return null;

  const rows = stages.map((stage, index) => {
    const value = Number(stage.value);
    const prev = index === 0 ? value : Number(stages[index - 1].value);
    const stepPct = prev ? (value / prev) * 100 : null;
    return {
      label: stage.label,
      value,
      stepPct: index === 0 ? null : stepPct,
      cumulativePct: (value / original) * 100,
      // Drop is what we rank on — the biggest single leak.
      drop: index === 0 || stepPct == null ? 0 : 100 - stepPct
    };
  });

  const worst = rows.reduce(
    (acc, row, index) => (row.drop > rows[acc].drop ? index : acc),
    0
  );

  // A leaking stage is the start of a question, not the end of one. Clicking
  // asks about the people lost AT that step, which is the thing the user
  // actually wants after seeing where the drop is.
  const askAbout = row => {
    if (copilotStore.streaming || row.stepPct == null) return;
    const lost = Math.round(rows[rows.indexOf(row) - 1].value - row.value);
    if (lost <= 0) return;
    // Deliberately no title suffix: block.title is the generic tool heading
    // ("Sequence performance"), so appending it produced "…in Sequence
    // performance", which names nothing the user recognises. The conversation
    // already establishes which sequence this is about.
    copilotStore.sendUserMessage(
      `Show me the ${lost} contacts who reached "${
        rows[rows.indexOf(row) - 1].label
      }" but not "${row.label}"`
    );
  };

  return (
    <div className="cpVisual">
      {block.title && <div className="cpVisual__title">{block.title}</div>}
      <ol className="cpFunnel">
        {rows.map((row, index) => (
          <li
            className={`cpFunnel__stage${
              index === worst && row.drop > 0 ? " is-worst" : ""
            }${index > 0 ? " is-clickable" : ""}`}
            key={`${row.label}-${index}`}
            role={index > 0 ? "button" : undefined}
            tabIndex={index > 0 ? 0 : undefined}
            onClick={index > 0 ? () => askAbout(row) : undefined}
            onKeyDown={
              index > 0
                ? e => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      askAbout(row);
                    }
                  }
                : undefined
            }
          >
            <div className="cpFunnel__meta">
              <span className="cpFunnel__label">{row.label}</span>
              <span className="cpFunnel__count">
                {formatMetric(row.value)}
              </span>
            </div>

            <div className="cpFunnel__barWrap">
              <div
                className="cpFunnel__bar"
                style={{ width: `${Math.max(row.cumulativePct, 1.5)}%` }}
              />
            </div>

            <div className="cpFunnel__pcts">
              {/* "Entered" belongs to the FIRST stage only. A later stage can
                  also have a null stepPct — when the stage before it is 0
                  there is no ratio to show — and labelling that "Entered" read
                  as if the funnel restarted there. */}
              {index === 0 ? (
                <span className="cpFunnel__step">Entered</span>
              ) : row.stepPct == null ? (
                <span className="cpFunnel__step cpFunnel__step--none">
                  No one reached the previous step
                </span>
              ) : (
                <span className="cpFunnel__step">
                  {`${formatMetric(row.stepPct, { unit: "%" })} from previous`}
                </span>
              )}
              {index > 0 && (
                <span className="cpFunnel__cumulative">
                  {`${formatMetric(row.cumulativePct, { unit: "%" })} of all`}
                </span>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
});

export default FunnelBlock;
